import React, { useState, useMemo, lazy, Suspense, memo, useCallback } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  InputBase,
  IconButton,
  Tabs,
  Tab,
  useTheme,
  Grid as MuiGrid,
  alpha,
  Chip,
  Divider,
  CircularProgress,
} from "@mui/material";
import {
  Search,
  TrendingUp,
  NewReleases,
  Language,
  Widgets,
  FlashOn,
  Flag,
  LocalMovies,
  Movie,
  TheaterComedy,
  Terrain,
  EmojiFlags,
  Public,
} from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import api from "../api/axios";
import { keyframes } from "@emotion/react";
import StructuredData from "../components/StructuredData";
import { generateMovieListSchema } from "../utils/schemaGenerator";
import debounce from 'lodash.debounce';

// Lazy load components with error handling and retry
const MovieCard = lazy(() =>
  import("../components/MovieCard").catch((err) => {
    console.error("Error loading MovieCard:", err);
    return import("../components/MovieCard");  // Retry once
  })
);

const MarketingBanner = memo(lazy(() =>
  import("../components/marketing/MarketingBanner.jsx")
));

const MarketingSidebar = memo(lazy(() =>
  import("../components/marketing/MarketingSidebar.jsx")
));

// Optimized loading fallback
const LoadingFallback = memo(() => (
  <Box sx={{ p: 2, textAlign: "center" }}>
    <CircularProgress size={24} />
  </Box>
));

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideIn = keyframes`
  from { transform: translateX(-20px); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
`;

const categories = [
  { id: "ALL_MOVIES", label: "All Movies", icon: <Movie /> },
  { id: "NEWLY_ADDED", label: "Newly Added", icon: <NewReleases /> },
  { id: "HOLLYWOOD", label: "Hollywood", icon: <Public /> },
  { id: "BOLLYWOOD", label: "Bollywood", icon: <TheaterComedy /> },
  { id: "SOUTH_INDIAN", label: "South Indian", icon: <LocalMovies /> },
  { id: "LATEST_RELEASES", label: "Latest Releases", icon: <FlashOn /> },
  { id: "TRENDING", label: "Trending Now", icon: <TrendingUp /> },
  { id: "NEPALI", label: "Nepali", icon: <Terrain /> },
  { id: "KOREAN", label: "Korean", icon: <Language /> },
  { id: "JAPANESE", label: "Japanese", icon: <EmojiFlags /> },
  { id: "ITALIAN", label: "Italian", icon: <Flag /> },
  { id: "OTHERS", label: "Others", icon: <Widgets /> },
];

const genres = [
  'Action',
  'Adventure',
  'Animation',
  'Comedy',
  'Drama',
  'Horror',
  'Romance',
  'Sci-Fi',
  'Thriller',
  'Mystery',
  'Documentary',
  'Fantasy',
  'Biography',
  'Crime',
  'Others',
];

const Home = () => {
  const theme = useTheme();
  const [selectedCategory, setSelectedCategory] = useState("ALL_MOVIES");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedGenres, setSelectedGenres] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);

  // Debounced search handler
  const debouncedSearch = useMemo(() => debounce((query) => {
    setSearchQuery(query);
    setCurrentPage(1);
  }, 300), []);

  const handleSearch = useCallback((event) => {
    debouncedSearch(event.target.value);
  }, [debouncedSearch]);

  // Optimized query with proper caching
  const {
    data: moviesData = { movies: [], totalPages: 0 },
    isLoading,
    error,
  } = useQuery({
    queryKey: ["movies", selectedCategory, searchQuery, selectedGenres, currentPage],
    queryFn: () =>
      api.get("/api/movies", {
        params: {
          page: currentPage,
          limit: 12,
          category: selectedCategory,
          search: searchQuery,
          genres: selectedGenres.join(","),
        },
      }).then((res) => res.data),
    keepPreviousData: true,
    staleTime: 1000 * 60 * 5, // 5 minutes
    cacheTime: 1000 * 60 * 30, // 30 minutes
    retry: 2,
    refetchOnWindowFocus: false,
  });

  // Add image loading optimization
  const imageLoader = ({ src, width, quality = 75 }) => {
    return `${src}?w=${width}&q=${quality}`;
  };

  // Memoized handlers
  const handleGenreToggle = useCallback((genre) => {
    setSelectedGenres((prev) =>
      prev.includes(genre) ? prev.filter((g) => g !== genre) : [...prev, genre]
    );
    setCurrentPage(1);
  }, []);

  const handleCategoryChange = useCallback((_, value) => {
    setSelectedCategory(value);
    setSelectedGenres([]);
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((newPage) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // Memoized computations
  const memoizedMovies = useMemo(
    () =>
      moviesData.movies.map((movie) => ({
        ...movie,
        posterUrl: imageLoader({ src: movie.posterUrl, width: 300 }),
      })),
    [moviesData.movies]
  );

  const filteredMovies = useMemo(() => memoizedMovies || [], [memoizedMovies]);

  const movieListSchema = useMemo(() => {
    if (!filteredMovies?.length) return null;
    return generateMovieListSchema(filteredMovies);
  }, [filteredMovies]);

  // Generate schema for website
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "MovieHub - Watch and download Latest Movies in HD Quality",
    description:
      "Want to download the Latest Bollywood Movies, Latest South Indian Movies, and Hollywood Movies in HD, from Thalapathy Vijay, Rashmika, moviehub is there for you",
    url: "https://moviehub888.xyz",
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: "https://moviehub888.xyz/search?q={search_term_string}",
      },
      "query-input": "required name=search_term_string",
    },
  };

  // Add virtual scrolling for movie grid
  const MovieGrid = memo(({ movies }) => {
    return (
      <Grid container spacing={2}>
        {movies.map((movie) => (
          <Grid item xs={6} sm={4} md={3} lg={2.4} key={movie._id}>
            <Suspense fallback={<LoadingFallback />}>
              <MovieCard movie={movie} hideDownload loading="lazy" />
            </Suspense>
          </Grid>
        ))}
      </Grid>
    );
  });

  const { data: activePromotions = [] } = useQuery({
    queryKey: ["activePromotions", "sidebar"],
    queryFn: async () => {
      try {
        const response = await api.get("/api/promotions/active");
        return response.data.filter((promo) => promo.position === "sidebar");
      } catch (error) {
        console.error("Error fetching active promotions:", error);
        return [];
      }
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const hasActiveAds = activePromotions.length > 0;

  return (
    <>
      <Helmet>
        <title>MovieHub - Watch and download Latest Movies in HD </title>
        <meta
          name="description"
          content="Watch and download the Latest Bollywood Movies, Latest South Indian Movies, and Hollywood Movies in HD, from Thalapathy Vijay, Rashmika, Allu Arjun, Ram, etc"
        />
        <meta
          name="keywords"
          content="Bollywood Movies, South Indian Movies, Hollywood Movies, Download Movies, HD Quality Movies, 
  Latest Movie Releases, Free Movie Downloads, MovieHub ,Hdhub4u ,filmyfly, south movie new ,filmy zilla ,hanuman, filmy4wap, 123 movies ,Pikashow,Top movieMost awaited Indian movies,Latest south Indian movies, How to download latest movies, new south movie"
        />
        <link rel="canonical" href={`https://moviehub888.xyz`} />

        <meta
          property="og:title"
          content="MovieHub - Watch and download Latest Movies in HD Quality"
        />
        <meta
          property="og:description"
          content="Watch and download the Latest Bollywood Movies, Latest South Indian Movies, and Hollywood Movies in HD, from Thalapathy Vijay, Rashmika, Allu Arjun, Ram, etc"
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://moviehub888.xyz" />
        <meta property="og:image" content="https://moviehub888.xyz/movie.svg" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="MovieHub - Watch and download Latest Movies in HD Quality"
        />
        <meta
          name="twitter:description"
          content="Watch and download the Latest Bollywood Movies, Latest South Indian Movies, and Hollywood Movies in HD, from Thalapathy Vijay, Rashmika, Allu Arjun, Ram, etc"
        />
        <meta
          name="twitter:image"
          content="https://moviehub888.xyz/movie.svg"
        />

        {/* Website Schema */}
        <script type="application/ld+json">
          {JSON.stringify(websiteSchema)}
        </script>

        {/* Movie List Schema - only if movies exist */}
        {movieListSchema && (
          <script type="application/ld+json">
            {JSON.stringify(movieListSchema)}
          </script>
        )}
      </Helmet>

      <Box
        sx={{
          minHeight: "100vh",
          background: "#000000",
          backgroundImage: `
            linear-gradient(to bottom, 
              rgba(0, 0, 0, 0.95), 
              rgba(16, 16, 32, 0.98),
              rgba(0, 0, 0, 0.95)
            )
          `,
          position: "relative",
          pt: { xs: 2, sm: 4 },
          pb: { xs: 4, sm: 8 },
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `
              radial-gradient(circle at 20% 50%, rgba(76, 0, 255, 0.05) 0%, transparent 25%),
              radial-gradient(circle at 80% 50%, rgba(0, 183, 255, 0.05) 0%, transparent 25%)
            `,
            opacity: 0.8,
            zIndex: 0,
          },
        }}
      >
        <Container maxWidth="xl">
          <Box sx={{ position: "relative", zIndex: 1, width: "100%" }}>
            <Suspense fallback={<LoadingFallback />}>
              <MarketingBanner
                position="top"
                onClick={() => console.log("Top banner clicked")}
              />
            </Suspense>
          </Box>

          {/* Enhanced Hero Section */}
          <Box
            component={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            sx={{
              mb: { xs: 2, sm: 4 },
              textAlign: "center",
              position: "relative",
              zIndex: 1,
              "&::before": {
                content: '""',
                position: "absolute",
                top: -32,
                left: 0,
                right: 0,
                height: "100%",
                background:
                  "radial-gradient(circle at center, rgba(76, 0, 255, 0.1) 0%, transparent 70%)",
                filter: "blur(40px)",
                pointerEvents: "none",
              },
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            >
              <Typography
                variant="h2"
                component="h2"
                sx={{
                  fontWeight: 800,
                  letterSpacing: "-2px",
                  fontSize: { xs: "3rem", sm: "3.5rem", md: "4.5rem" },
                  background: "linear-gradient(to right, #fff, #a7b5ff)",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  textShadow: "0 0 40px rgba(76, 0, 255, 0.3)",
                  mb: 2,
                  mt: 3,
                }}
              >
                Moviehub
              </Typography>
              <Typography
                variant="h1"
                component="h1"
                sx={{
                  color: "rgba(255, 255, 255, 0.9)",
                  mb: 6,
                  fontWeight: 400,
                  fontSize: { xs: "1.1rem", sm: "1.3rem", md: "1.4rem" },
                  maxWidth: "800px",
                  mx: "auto",
                  textShadow: "0 2px 10px rgba(0, 0, 0, 0.3)",
                }}
              >
                Download Latest Bollywood, South Indian, and Hollywood Movies.
              </Typography>
            </motion.div>

            {/* Enhanced Search Bar */}
            <Paper
              component={motion.div}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              elevation={0}
              sx={{
                p: "6px 8px",
                display: "flex",
                alignItems: "center",
                width: { xs: "95%", sm: "80%", md: "60%" },
                mx: "auto",
                mb: 2,
                borderRadius: "50px",
                background: "rgba(255, 255, 255, 0.05)",
                backdropFilter: "blur(20px)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  background: "rgba(255, 255, 255, 0.08)",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  transform: "translateY(-2px)",
                  boxShadow: "0 8px 30px rgba(0, 0, 0, 0.2)",
                },
              }}
            >
              <InputBase
                sx={{
                  ml: 3,
                  flex: 1,
                  fontSize: "1.1rem",
                  color: "#fff",
                  "& input": {
                    padding: "16px 0",
                    "&::placeholder": {
                      color: "rgba(255, 255, 255, 0.5)",
                      opacity: 1,
                      transition: "opacity 0.2s",
                    },
                    "&:focus::placeholder": {
                      opacity: 0.3,
                    },
                  },
                }}
                placeholder="Search for movies..."
                onChange={handleSearch}
              />
              <IconButton
                sx={{
                  p: "16px",
                  mr: 1,
                  color: "rgba(255, 255, 255, 0.7)",
                  transition: "all 0.3s",
                  "&:hover": {
                    color: "#fff",
                    transform: "scale(1.1) rotate(5deg)",
                    background: "rgba(255, 255, 255, 0.1)",
                  },
                }}
              >
                <Search />
              </IconButton>
            </Paper>
          </Box>

          <Grid container spacing={4}>
            {/* Main Content */}
            <Grid item xs={12} md={hasActiveAds ? 9 : 12}>
              {/* Categories */}
              <Paper
                elevation={0}
                sx={{
                  mb: 4,
                  borderRadius: "20px",
                  overflow: "hidden",
                  background: "rgba(255, 255, 255, 0.03)",
                  backdropFilter: "blur(20px)",
                  border: "1px solid rgba(255, 255, 255, 0.05)",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    background: "rgba(255, 255, 255, 0.05)",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                  },
                }}
              >
                <Tabs
                  value={selectedCategory}
                  onChange={handleCategoryChange}
                  variant="scrollable"
                  scrollButtons={false}
                  sx={{
                    px: 2,
                    "& .MuiTab-root": {
                      minHeight: 64,
                      fontSize: "0.95rem",
                      fontWeight: 500,
                      color: "rgba(255, 255, 255, 0.7)",
                      transition: "all 0.2s",
                      mx: 1,
                      "&:hover": {
                        color: "#fff",
                        background: "rgba(255, 255, 255, 0.05)",
                        transform: "translateY(-2px)",
                      },
                      "& .MuiSvgIcon-root": {
                        transition: "transform 0.2s",
                        mr: 1,
                        fontSize: "1.4rem",
                      },
                      "&:hover .MuiSvgIcon-root": {
                        transform: "scale(1.1)",
                      },
                    },
                    "& .Mui-selected": {
                      color: "#fff !important",
                      fontWeight: 600,
                      "& .MuiSvgIcon-root": {
                        transform: "scale(1.1)",
                        color: "#fff",
                      },
                    },
                    "& .MuiTabs-indicator": {
                      height: 3,
                      borderRadius: "3px 3px 0 0",
                      background: "linear-gradient(to right, #4B6CB7, #182848)",
                    },
                  }}
                >
                  {categories.map((category) => (
                    <Tab
                      key={category.id}
                      value={category.id}
                      label={category.label}
                      icon={category.icon}
                      iconPosition="start"
                    />
                  ))}
                </Tabs>
              </Paper>

              {/* Genre Filters */}
              <Box
                component={motion.div}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                sx={{
                  mb: 4,
                  display: "flex",
                  gap: 1.5,
                  flexWrap: "wrap",
                  px: 1,
                }}
              >
                {genres.map((genre, index) => (
                  <motion.div
                    key={genre}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.05 }}
                  >
                    <Chip
                      label={genre}
                      clickable
                      color={
                        selectedGenres.includes(genre) ? "primary" : "default"
                      }
                      onClick={() => handleGenreToggle(genre)}
                      sx={{
                        borderRadius: "50px",
                        px: 0.5,
                        height: 40,
                        fontSize: "0.9rem",
                        fontWeight: selectedGenres.includes(genre) ? 600 : 400,
                        color: selectedGenres.includes(genre)
                          ? "#fff"
                          : "rgba(255, 255, 255, 0.7)",
                        background: selectedGenres.includes(genre)
                          ? "linear-gradient(45deg, #4B6CB7, #182848)"
                          : "rgba(255, 255, 255, 0.05)",
                        border: "1px solid",
                        borderColor: selectedGenres.includes(genre)
                          ? "rgba(255, 255, 255, 0.2)"
                          : "rgba(255, 255, 255, 0.05)",
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: "0 5px 15px rgba(0, 0, 0, 0.2)",
                          background: selectedGenres.includes(genre)
                            ? "linear-gradient(45deg, #182848, #4B6CB7)"
                            : "rgba(255, 255, 255, 0.1)",
                          borderColor: "rgba(255, 255, 255, 0.2)",
                        },
                      }}
                    />
                  </motion.div>
                ))}
              </Box>

              {/* Movies Grid */}
              {isLoading ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: 200,
                  }}
                >
                  <CircularProgress />
                </Box>
              ) : moviesData.movies.length > 0 ? (
                <MovieGrid movies={filteredMovies} />
              ) : (
                <Box
                  component={motion.div}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  sx={{
                    textAlign: "center",
                    py: 8,
                    px: 2,
                    borderRadius: 2,
                    background: "rgba(255, 255, 255, 0.03)",
                    backdropFilter: "blur(10px)",
                    border: "1px solid rgba(255, 255, 255, 0.05)",
                  }}
                >
                  <Movie
                    sx={{
                      fontSize: 60,
                      color: "rgba(255, 255, 255, 0.2)",
                      mb: 2,
                    }}
                  />
                  <Typography
                    variant="h5"
                    sx={{
                      color: "rgba(255, 255, 255, 0.9)",
                      fontWeight: 500,
                      mb: 1,
                    }}
                  >
                    No Movies Found
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: "rgba(255, 255, 255, 0.6)",
                      maxWidth: 500,
                      mx: "auto",
                    }}
                  >
                    {searchQuery
                      ? `No movies found matching "${searchQuery}"`
                      : selectedGenres.length > 0
                      ? `No movies found in the selected genres: ${selectedGenres.join(
                          ", "
                        )}`
                      : `No movies available in this category yet. Please check back later.`}
                  </Typography>
                </Box>
              )}

              {/* Pagination */}
              {moviesData.totalPages > 1 && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: 2,
                    mt: 4,
                    mb: 4,
                  }}
                >
                  <IconButton
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    sx={{
                      bgcolor: "#B0E2FF",
                      color: "#000",
                      width: "25px", // Set a smaller width
                      height: "25px",
                      "&:hover": {
                        bgcolor: "#D1EEFF",
                      },
                      "&.Mui-disabled": {
                        bgcolor: "rgba(176, 226, 255, 0.3)",
                      },
                    }}
                  >
                    <Box
                      component="span"
                      sx={{ fontSize: "1.2rem", fontWeight: "bold" }}
                    >
                      ←
                    </Box>
                  </IconButton>

                  <Typography
                    variant="h6"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      color: "rgba(255, 255, 255, 0.9)",
                      fontWeight: 250,
                    }}
                  >
                    Page {currentPage} of {moviesData.totalPages}
                  </Typography>

                  <IconButton
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === moviesData.totalPages}
                    sx={{
                      bgcolor: "#87CEEB",
                      color: "#000",
                      "&:hover": {
                        bgcolor: "#a3daef",
                      },
                      "&.Mui-disabled": {
                        bgcolor: "rgba(135, 206, 235, 0.3)",
                      },
                    }}
                  >
                    <Box
                      component="span"
                      sx={{ fontSize: "1.2rem", fontWeight: "bold" }}
                    >
                      →
                    </Box>
                  </IconButton>
                </Box>
              )}
            </Grid>

            {/* Sidebar */}
            {hasActiveAds && (
              <Grid item xs={12} md={3}>
                <Box
                  sx={{
                    position: "sticky",
                    top: 20,
                    transition: "all 0.3s ease-in-out",
                  }}
                >
                  <Suspense fallback={<LoadingFallback />}>
                    <MarketingSidebar />
                  </Suspense>
                </Box>
              </Grid>
            )}
          </Grid>

          <Box sx={{ position: "relative", zIndex: 1, width: "100%" }}>
            <Suspense fallback={<LoadingFallback />}>
              <MarketingBanner
                position="bottom"
                onClick={() => console.log("Bottom banner clicked")}
              />
            </Suspense>
          </Box>
        </Container>
        {/* Disclaimer Section */}
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          sx={{
            mt: 8,
            mb: 4,
            p: 3,
            borderRadius: 2,
            background:
              "linear-gradient(145deg, rgba(25, 118, 210, 0.05) 0%, rgba(156, 39, 176, 0.05) 100%)",
            backdropFilter: "blur(10px)",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: (theme) =>
              `0 4px 20px ${alpha(theme.palette.common.black, 0.05)}`,
          }}
        >
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              color: "#fff",
              fontFamily: "Poppins, sans-serif",
              fontWeight: 500,
              mb: 2,
              textAlign: "center",
              fontSize: { xs: "1.1rem", sm: "1.25rem" },
            }}
          >
            Legal Disclaimer
          </Typography>
          <Divider sx={{ mb: 2, opacity: 0.1 }} />
          <MuiGrid container spacing={3}>
            <MuiGrid item xs={12} md={6}>
              <Typography
                variant="body2"
                paragraph
                sx={{
                  color: "#fff",
                  lineHeight: 1.8,
                  fontFamily: "Roboto, sans-serif",
                  textAlign: { xs: "left", md: "justify" },
                  fontSize: { xs: "0.875rem", sm: "0.9rem" },
                }}
              >
                <Box
                  component="span"
                  sx={{ color: "#42a5f5", fontWeight: 500 }}
                >
                  Respecting Copyright Laws:
                </Box>{" "}
                While we provide links to Content, we encourage our users to
                respect copyright laws in their respective countries. moviehub
                does not host any content on its servers but connects users to
                other responsible Third-Party Sites.
              </Typography>
            </MuiGrid>
            <MuiGrid item xs={12} md={6}>
              <Typography
                variant="body2"
                paragraph
                sx={{
                  color: "#fff",
                  lineHeight: 1.8,
                  fontFamily: "Roboto, sans-serif",
                  textAlign: { xs: "left", md: "justify" },
                  fontSize: { xs: "0.875rem", sm: "0.9rem" },
                }}
              >
                <Box
                  component="span"
                  sx={{ color: "#42a5f5", fontWeight: 500 }}
                >
                  Disclaimer Notices:
                </Box>{" "}
                moviehub is not responsible for any direct or indirect actions
                taken by users concerning the content accessed through external
                links.
              </Typography>
            </MuiGrid>
          </MuiGrid>
        </Box>

        {/* Footer */}
        <Box
          component="footer"
          sx={{
            mt: 8,
            py: 8,
            background: "rgba(0, 0, 0, 0.5)",
            backdropFilter: "blur(20px)",
            borderTop: "1px solid rgba(255, 255, 255, 0.05)",
            position: "relative",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "1px",
              background:
                "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent)",
            },
          }}
        >
          <Container maxWidth="lg">
            <Grid container spacing={6}>
              <Grid item xs={12} md={4}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{
                    fontWeight: 700,
                    color: "#fff",
                    mb: 3,
                  }}
                ></Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "rgba(255, 255, 255, 0.7)",
                    lineHeight: 1.8,
                  }}
                >
                  MovieHub is your premier destination for high-quality movies,
                  series, etc. downloads. We offer a vast collection of
                  Hollywood, Bollywood, and South Indian movies, all in HD
                  quality. Our platform is designed to provide the best movie
                  downloading experience with fast servers and regular
                  updates. Watch and download latest movies like Animal, Pushpa
                  2, Squid Games, etc.
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{
                    fontWeight: 700,
                    color: "#fff",
                    mb: 3,
                  }}
                >
                  Features
                </Typography>
                <Typography
                  variant="body2"
                  component="div"
                  sx={{
                    color: "rgba(255, 255, 255, 0.7)",
                    lineHeight: 2.2,
                    "& span": {
                      display: "block",
                      position: "relative",
                      paddingLeft: "20px",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        left: 0,
                        top: "50%",
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        background: "rgba(255, 255, 255, 0.4)",
                        transform: "translateY(-50%)",
                      },
                    },
                  }}
                >
                  <span>HD Quality Movies</span>
                  <span>Fast Download Speeds</span>
                  <span>Multiple Language Options</span>
                  <span>Regular Updates</span>
                  <span>User-Friendly Interface</span>
                </Typography>
              </Grid>
            </Grid>
          </Container>
        </Box>
      </Box>
    </>
  );
};

export default Home;